/**
 * Componente: StatCard
 * Card de estatística para dashboard
 */

import React from 'react';
import { LucideIcon } from 'lucide-react';

type StatCardProps = {
  label: string;
  value: string | number;
  description?: string;
  change?: {
    value: number;
    isPositive: boolean;
  };
  icon: LucideIcon;
  color: 'teal' | 'blue' | 'amber' | 'rose' | 'green';
};

const colorConfig = {
  teal: {
    bg: 'bg-teal-50',
    icon: 'text-teal-600',
    text: 'text-teal-600',
    change: 'bg-teal-100 text-teal-700',
  },
  blue: {
    bg: 'bg-blue-50',
    icon: 'text-blue-600',
    text: 'text-blue-600',
    change: 'bg-blue-100 text-blue-700',
  },
  amber: {
    bg: 'bg-amber-50',
    icon: 'text-amber-600',
    text: 'text-amber-600',
    change: 'bg-amber-100 text-amber-700',
  },
  rose: {
    bg: 'bg-rose-50',
    icon: 'text-rose-600',
    text: 'text-rose-600',
    change: 'bg-rose-100 text-rose-700',
  },
  green: {
    bg: 'bg-green-50',
    icon: 'text-green-600',
    text: 'text-green-600',
    change: 'bg-green-100 text-green-700',
  },
};

export function StatCard({ label, value, description, change, icon: Icon, color }: StatCardProps) {
  const config = colorConfig[color];

  return (
    <div className={`${config.bg} rounded-lg p-6 border border-gray-200`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-sm text-gray-600 mb-1 uppercase tracking-wide">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 ${config.bg} rounded-lg`}>
          <Icon className={`w-6 h-6 ${config.icon}`} />
        </div>
      </div>

      {description && (
        <p className="mt-2 text-xs text-gray-500 italic flex items-center gap-1">
          {description}
        </p>
      )}

      {change && (
        <div className={`mt-3 inline-block px-3 py-1 rounded-full text-xs font-semibold ${config.change}`}>
          {change.isPositive ? '↑' : '↓'} {Math.abs(change.value)}%
        </div>
      )}
    </div>
  );
}
